import { useState } from "react";
import { Link, useParams, useSearchParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Bookmark, Briefcase, MapPin, Tag, Clock, MessageCircle,
  ShieldCheck, ShoppingCart, TrendingUp, TrendingDown, Send, Inbox,
} from "lucide-react";
import { useBloggers } from "@/shared/api/wb";
import { useDraftStore } from "@/shared/store/draft";
import { Avatar, Skeleton, PlatformIcon, CartPickerModal, Toast } from "@/shared/ui";
import { fmtNum } from "@/shared/lib/format";
import { cn } from "@/shared/lib/cn";
import {
  CardRight, normalizePlatform, PLATFORM_LABELS,
  getFormats, getDefaultPlatformForType,
  POSEV_PLATFORMS, NATIV_PLATFORMS,
  type CartSelection, type FormatType, type PlatformKey as FmtPlatformKey,
} from "@/features/blogger-card";
import { InboxModal } from "@/pages/InboxModal";
import { generateProfile } from "./mockData";
import type { PlatformKey } from "./mockData";
import { AudienceTab } from "./tabs/AudienceTab";
import { ReachTab } from "./tabs/ReachTab";
import { FraudTab } from "./tabs/FraudTab";
import { PostsTab } from "./tabs/PostsTab";
import { HistoryTab } from "./tabs/HistoryTab";
import { ReviewsTab } from "./tabs/ReviewsTab";
import { SimilarBloggers } from "./SimilarBloggers";

// ─── Shared ───────────────────────────────────────────────────────────────────

const AVATAR_GRADS: Record<PlatformKey, [string, string]> = {
  tg:   ["#9FE1CB", "#085041"],
  vk:   ["#B5D4F4", "#0C447C"],
  yt:   ["#F5C4B3", "#712B13"],
  dzen: ["#E8E8E8", "#444"],
  tt:   ["#E0E0E5", "#1A1D22"],
  lk:   ["#FFE8D5", "#7A2200"],
  max:  ["#E9DEFF", "#3D179A"],
};

const PLATFORM_ACTIVE_STYLE: Record<PlatformKey, { bg: string; text: string }> = {
  tg:   { bg: "#EAF3DE", text: "#27500A" },
  vk:   { bg: "#E6F1FB", text: "#185FA5" },
  yt:   { bg: "#FFE4E4", text: "#8B0000" },
  dzen: { bg: "#F5F5F5", text: "#444" },
  tt:   { bg: "#F0F0F2", text: "#2C3036" },
  lk:   { bg: "#FFF4EE", text: "#A63000" },
  max:  { bg: "#F3EEFF", text: "#5B2DBE" },
};

// ─── Quality score ring ───────────────────────────────────────────────────────

function QualityRing({ score }: { score: number }) {
  const r = 21; const circ = 2 * Math.PI * r;
  const arc = (score / 100) * circ;
  const color = score >= 80 ? "#639922" : score >= 60 ? "#EF9F27" : "#E24B4A";
  return (
    <div className="flex flex-col items-center gap-0.5 flex-shrink-0">
      <svg width={48} height={48} viewBox="0 0 48 48">
        <circle cx={24} cy={24} r={r} fill="none" stroke="rgba(28,28,28,0.08)" strokeWidth={5} />
        <circle cx={24} cy={24} r={r} fill="none" stroke={color} strokeWidth={5}
          strokeDasharray={`${arc} ${circ}`} strokeLinecap="round" transform="rotate(-90 24 24)" />
        <text x={24} y={24} textAnchor="middle" dominantBaseline="central"
          className="font-display font-bold" style={{ fontSize: 12, fill: color }}>{score}</text>
      </svg>
      <span className="text-[9px] text-ink-40 font-display font-semibold uppercase tracking-wider">quality</span>
    </div>
  );
}

// ─── Network switcher ─────────────────────────────────────────────────────────

const PLATFORM_TO_ICON: Record<PlatformKey, "telegram" | "vk" | "youtube" | "dzen" | "tiktok" | null> = {
  tg: "telegram", vk: "vk", yt: "youtube", dzen: "dzen", tt: "tiktok", lk: null, max: null,
};

function NetworkSwitch({ platforms, selected, onChange }: {
  platforms: PlatformKey[];
  selected: PlatformKey;
  onChange: (p: PlatformKey) => void;
}) {
  if (platforms.length < 2) return null;
  return (
    <div className="inline-flex rounded-full p-0.5 gap-0.5" style={{ background: "rgba(28,28,28,0.06)" }}>
      {platforms.map((p) => {
        const active = p === selected;
        const s = PLATFORM_ACTIVE_STYLE[p];
        const icon = PLATFORM_TO_ICON[p];
        return (
          <button key={p} onClick={() => onChange(p)}
            className="h-7 px-3 rounded-full text-[11px] font-display font-bold transition-colors inline-flex items-center gap-1.5"
            style={active ? { background: s.bg, color: s.text } : { color: "var(--fg-3, rgba(28,28,28,0.4))" }}>
            {icon && <PlatformIcon platform={icon} size={12} mono={!active} />}
            {PLATFORM_LABELS[p] ?? p}
          </button>
        );
      })}
    </div>
  );
}

// ─── Campaign banner ──────────────────────────────────────────────────────────

function CampaignBanner({ productName }: { productName: string }) {
  return (
    <div className="flex items-center gap-3 px-4 py-3 rounded-md mb-4"
      style={{ background: "#EAF3DE18", border: "0.5px solid #C0DD97" }}>
      <Briefcase size={14} style={{ color: "#639922", flexShrink: 0 }} />
      <span className="text-[12.5px] text-ink-60">
        Просмотр в контексте РК · продукт: <strong className="text-ink">{productName}</strong>
      </span>
    </div>
  );
}

// ─── Key stats ────────────────────────────────────────────────────────────────

function StatCard({ label, value, delta, deltaPositive }: {
  label: string; value: string; delta: string; deltaPositive: boolean;
}) {
  const DeltaIcon = deltaPositive ? TrendingUp : TrendingDown;
  return (
    <div className="rounded-md bg-white border border-ink-12 p-3 flex flex-col gap-[5px]">
      <div className="text-[10px] font-display font-semibold uppercase tracking-wider text-ink-40 leading-none">{label}</div>
      <div className="font-display font-[500] text-[20px] leading-tight tracking-tight">{value}</div>
      <div className="flex items-center gap-1 text-[11px]" style={{ color: deltaPositive ? "#27500A" : "#A32D2D" }}>
        <DeltaIcon size={10} />
        <span className="leading-none">{delta}</span>
      </div>
    </div>
  );
}

// ─── Header ───────────────────────────────────────────────────────────────────

type ProfileHeaderProps = {
  name: string; full: string;
  platform: PlatformKey; platforms: PlatformKey[];
  selectedPlatform: PlatformKey; onPlatformChange: (p: PlatformKey) => void;
  qualityScore: number;
  geoLabel: string; category: string; yearsOnPlatform: number;
  responseHours: number; isVerified: boolean; completedCampaigns: number;
  description?: string | null;
  // format picker
  fmtType: FormatType; fmtPlatform: FmtPlatformKey; fmtFormatId: string;
  isInCart: boolean;
  onFmtType: (t: FormatType) => void;
  onFmtPlatform: (p: FmtPlatformKey) => void;
  onFmtFormat: (id: string) => void;
  onCartToggle: () => void;
};

function ProfileHeader({
  name, full, platform, platforms,
  selectedPlatform, onPlatformChange,
  qualityScore, geoLabel, category, yearsOnPlatform, responseHours,
  isVerified, completedCampaigns, description,
  fmtType, fmtPlatform, fmtFormatId, isInCart,
  onFmtType, onFmtPlatform, onFmtFormat, onCartToggle,
}: ProfileHeaderProps) {
  const grad = AVATAR_GRADS[normalizePlatform(platform) as PlatformKey] ?? ["#BFF93F", "#1C1C1C"];
  const responseColor = responseHours <= 6 ? "#639922" : undefined;

  return (
    <div className={cn(
      "bg-white rounded-lg border p-5 mb-4",
      isInCart ? "border-[#97C459] border-2" : "border-ink-12",
    )}>
      <div className="flex items-start gap-4">
        <Avatar name={full} size={58} grad={grad} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="font-display font-bold text-[17px]">{name}</span>
            {platforms.map((p) => {
              const s = PLATFORM_ACTIVE_STYLE[p];
              return (
                <span key={p} className="h-5 px-2 rounded-full text-[10px] font-display font-bold"
                  style={{ background: s.bg, color: s.text }}>
                  {PLATFORM_LABELS[p]}
                </span>
              );
            })}
          </div>
          <div className="flex items-center gap-2 flex-wrap mb-2">
            {isVerified && (
              <span className="inline-flex items-center gap-1 h-5 px-2 rounded-full text-[10px] font-display font-bold"
                style={{ background: "#EAF3DE", color: "#27500A" }}>
                <ShieldCheck size={10} /> Верифицирован
              </span>
            )}
            {completedCampaigns > 0 && (
              <span className="inline-flex items-center gap-1 h-5 px-2 rounded-full text-[10px] font-display font-bold"
                style={{ background: "#E6F1FB", color: "#185FA5" }}>
                {completedCampaigns} РК закрыто
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 text-[11px] text-ink-40 flex-wrap">
            <span className="flex items-center gap-1"><MapPin size={11} /> {geoLabel}</span>
            <span className="flex items-center gap-1"><Tag size={11} /> {category}</span>
            <span className="flex items-center gap-1"><Clock size={11} /> {yearsOnPlatform} лет на платформе</span>
            <span className="flex items-center gap-1" style={responseColor ? { color: responseColor } : {}}>
              <MessageCircle size={11} /> отвечает за {responseHours} ч
            </span>
          </div>
          {description && (
            <p className="mt-2.5 text-[13px] text-ink-60 leading-relaxed">{description}</p>
          )}
        </div>
        <div className="flex items-start gap-3 flex-shrink-0">
          <QualityRing score={qualityScore} />
          <button className="h-8 w-8 rounded-pill border border-ink-12 grid place-items-center hover:border-ink/40 mt-0.5 flex-shrink-0">
            <Bookmark size={13} className="text-ink-40" />
          </button>
        </div>
      </div>
      <div className="mt-4 flex items-center justify-between gap-4 flex-wrap">
        <NetworkSwitch platforms={platforms} selected={selectedPlatform} onChange={onPlatformChange} />
        <div className="border border-ink-12 rounded-lg overflow-hidden">
          <CardRight
            type={fmtType} platform={fmtPlatform} formatId={fmtFormatId}
            inCart={isInCart}
            onTypeChange={onFmtType} onPlatformChange={onFmtPlatform}
            onFormatChange={onFmtFormat} onCartToggle={onCartToggle}
          />
        </div>
      </div>
    </div>
  );
}

// ─── Profile tabs ─────────────────────────────────────────────────────────────

type TabKey = "audience" | "reach" | "fraud" | "posts" | "history" | "reviews";
const TAB_LABELS: Record<TabKey, string> = {
  audience: "Аудитория", reach: "Охваты", fraud: "Качество",
  posts: "Посты", history: "История РК", reviews: "Отзывы",
};
const PLATFORM_TABS: TabKey[] = ["audience", "reach", "fraud"];

function ProfileTabs({
  activeTab, onChange,
}: {
  activeTab: TabKey;
  onChange: (t: TabKey) => void;
}) {
  return (
    <div className="flex gap-0 border-b border-ink-12 mb-5">
      {(Object.keys(TAB_LABELS) as TabKey[]).map((t) => (
        <button key={t} onClick={() => onChange(t)}
          className={cn(
            "px-4 py-2.5 text-[13px] font-display font-semibold border-b-2 -mb-px transition-colors whitespace-nowrap",
            activeTab === t
              ? "border-[#639922] text-ink"
              : "border-transparent text-ink-40 hover:text-ink-60",
          )}>
          {TAB_LABELS[t]}
        </button>
      ))}
    </div>
  );
}

// ─── Root page ────────────────────────────────────────────────────────────────

export function BloggerProfile() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const ctx = searchParams.get("ctx") ?? "catalog";
  const isCampaignCtx = ctx === "campaign";

  const { data: allBloggers, isFetching } = useBloggers({} as never);
  const blogger = (allBloggers ?? []).find((b) => b.id === id);

  const draft = useDraftStore((s) => s.draft);
  const addBloggerFormat = useDraftStore((s) => s.addBloggerFormat);
  const removeBloggerFormat = useDraftStore((s) => s.removeBloggerFormat);
  const assignCreative = useDraftStore((s) => s.assignCreative);

  const [pickerSel, setPickerSel] = useState<CartSelection | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [inboxOpen, setInboxOpen] = useState<"write" | "list" | null>(null);

  const profile = blogger ? generateProfile(blogger) : null;
  const primaryKey = blogger ? (normalizePlatform(blogger.platform) as PlatformKey) : "tg";

  const [selectedPlatform, setSelectedPlatform] = useState<PlatformKey>(primaryKey);
  const [activeTab, setActiveTab] = useState<TabKey>("audience");

  // Format picker state
  const [fmtType, setFmtType] = useState<FormatType>("posev");
  const [fmtPlatform, setFmtPlatform] = useState<FmtPlatformKey>(primaryKey);
  const [fmtFormatId, setFmtFormatId] = useState<string>(() => getFormats(primaryKey, "posev")[0]?.id ?? "");

  const cartSels = id ? (draft.bloggerFormats[id] ?? []) : [];
  const isInCart = cartSels.some(s => s.platform === fmtPlatform && s.type === fmtType);

  function handleFmtType(t: FormatType) {
    const p = getDefaultPlatformForType(t, fmtPlatform);
    const fmts = getFormats(p, t);
    setFmtType(t);
    setFmtPlatform(p);
    setFmtFormatId(fmts[0]?.id ?? "");
  }

  function handleFmtPlatform(p: FmtPlatformKey) {
    const allowed = fmtType === "posev" ? POSEV_PLATFORMS : NATIV_PLATFORMS;
    if (!allowed.includes(p)) return;
    const fmts = getFormats(p, fmtType);
    setFmtPlatform(p);
    setFmtFormatId(fmts[0]?.id ?? "");
  }

  function handleCartToggle() {
    if (!id || !blogger) return;
    const formats = getFormats(fmtPlatform, fmtType);
    const fmt = formats.find(f => f.id === fmtFormatId) ?? formats[0];
    if (!fmt) return;
    if (isInCart) {
      removeBloggerFormat(id, fmtPlatform, fmtType);
    } else {
      const sel: CartSelection = {
        type: fmtType, platform: fmtPlatform,
        formatId: fmt.id, formatName: fmt.name,
        price: fmt.price, cpv: fmt.cpv, placementDate: "",
      };
      setPickerSel(sel);
    }
  }

  const handlePlatformChange = (p: PlatformKey) => {
    setSelectedPlatform(p);
    if (PLATFORM_TABS.includes(activeTab as TabKey)) {
      // keep tab, data will re-render
    }
  };

  if (isFetching) {
    return (
      <div className="px-7 pt-6 pb-14 max-w-[800px] mx-auto grid gap-4">
        <Skeleton className="h-7 w-32" />
        <Skeleton className="h-36 w-full rounded-lg" />
        <div className="grid grid-cols-5 gap-3">
          {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-24 rounded-md" />)}
        </div>
      </div>
    );
  }

  if (!blogger || !profile) {
    return (
      <div className="px-7 pt-6 pb-14 max-w-[800px] mx-auto">
        <div className="text-center py-14 text-ink-40">Блогер не найден</div>
      </div>
    );
  }

  const platformData = profile.platformStats[selectedPlatform] ?? Object.values(profile.platformStats)[0]!;
  const productName = draft?.productName || "ваш продукт";

  const stats = [
    {
      label: "Просмотры/пост",
      value: fmtNum(platformData.avgViews),
      delta: `${platformData.viewsDeltaPct > 0 ? "+" : ""}${platformData.viewsDeltaPct}% за 30 дн`,
      pos: platformData.viewsDeltaPct > 0,
    },
    {
      label: "ER",
      value: `${platformData.er}%`,
      delta: `ER ×${platformData.erVsMedian.toFixed(1)} к категории`,
      pos: true,
    },
    {
      label: "CPV",
      value: `${platformData.cpv} ₽`,
      delta: `лучше ${platformData.livePercentile}% категории`,
      pos: true,
    },
    {
      label: "Живая аудитория",
      value: `${platformData.liveAudience}%`,
      delta: platformData.liveAudience >= 80 ? "хороший показатель" : "умеренный",
      pos: platformData.liveAudience >= 80,
    },
    {
      label: "Постов/мес",
      value: String(platformData.postsPerMonth),
      delta: "регулярная активность",
      pos: true,
    },
  ];

  const cartCount = Object.values(draft.bloggerFormats).filter(s => s && s.length > 0).length;

  return (
    <div className="px-7 pt-6 pb-14 max-w-[800px] mx-auto">
      {/* Back + chat actions + cart counter */}
      <div className="flex items-center justify-between mb-4">
        <button onClick={() => navigate(-1)}
          className="flex items-center gap-1.5 text-[13px] text-ink-40 hover:text-ink">
          <ArrowLeft size={14} /> Назад
        </button>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setInboxOpen("write")}
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-pill border border-ink-12 text-[12px] font-display font-semibold text-ink-60 hover:text-ink hover:border-ink/40 transition-colors"
          >
            <Send size={13} /> Написать
          </button>
          <button
            onClick={() => setInboxOpen("list")}
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-pill border border-ink-12 text-[12px] font-display font-semibold text-ink-60 hover:text-ink hover:border-ink/40 transition-colors"
          >
            <Inbox size={13} /> Лента чатов
          </button>
          {cartCount > 0 && (
            <Link
              to="/campaign/new/bloggers"
              className="inline-flex items-center gap-2 h-9 px-4 rounded-pill bg-lime text-[#1A3A05] font-display font-bold text-[13px] hover:bg-lime/90 transition-colors shadow-sm"
            >
              <ShoppingCart size={14} />
              В РК
              <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#1A3A05] text-lime text-[11px] font-mono">
                {cartCount}
              </span>
            </Link>
          )}
        </div>
      </div>

      {/* Campaign banner */}
      {isCampaignCtx && <CampaignBanner productName={productName} />}

      {/* Header */}
      <ProfileHeader
        name={blogger.name}
        full={blogger.full}
        platform={normalizePlatform(blogger.platform) as PlatformKey}
        platforms={profile.platforms}
        selectedPlatform={selectedPlatform}
        onPlatformChange={handlePlatformChange}
        qualityScore={profile.qualityScore}
        geoLabel={profile.geoLabel}
        category={blogger.cats[0] ?? "Разное"}
        yearsOnPlatform={profile.yearsOnPlatform}
        responseHours={profile.responseHours}
        isVerified={profile.isVerified}
        completedCampaigns={profile.completedCampaigns}
        description={blogger.description}
        fmtType={fmtType} fmtPlatform={fmtPlatform} fmtFormatId={fmtFormatId}
        isInCart={isInCart}
        onFmtType={handleFmtType} onFmtPlatform={handleFmtPlatform}
        onFmtFormat={setFmtFormatId} onCartToggle={handleCartToggle}
      />

      {/* Key stats */}
      <div className="grid grid-cols-5 gap-3 mb-6">
        {stats.map((s) => (
          <StatCard key={s.label} label={s.label} value={s.value} delta={s.delta} deltaPositive={s.pos} />
        ))}
      </div>

      {/* Tabs */}
      <ProfileTabs activeTab={activeTab} onChange={setActiveTab} />

      <div className="min-h-[300px]">
        {activeTab === "audience" && (
          <AudienceTab data={platformData} campaignContext={isCampaignCtx} productName={productName} />
        )}
        {activeTab === "reach" && <ReachTab data={platformData} />}
        {activeTab === "fraud" && <FraudTab data={platformData} />}
        {activeTab === "posts" && <PostsTab posts={profile.posts} />}
        {activeTab === "history" && <HistoryTab history={profile.history} />}
        {activeTab === "reviews" && <ReviewsTab reviews={profile.reviews} />}
      </div>

      <SimilarBloggers bloggers={profile.similar} context={isCampaignCtx ? "campaign" : "catalog"} />
      {toast && <Toast message={toast} onHide={() => setToast(null)} />}

      {pickerSel && blogger && (
        <CartPickerModal
          blogger={blogger}
          cartSelection={pickerSel}
          onConfirm={variantId => {
            addBloggerFormat(blogger.id, pickerSel);
            if (variantId) assignCreative(blogger.id, variantId);
            setPickerSel(null);
            setToast(`${blogger.name} добавлен в РК`);
          }}
          onClose={() => setPickerSel(null)}
        />
      )}
      {inboxOpen && (
        <InboxModal
          open
          onClose={() => setInboxOpen(null)}
          initialBloggerId={inboxOpen === "write" ? id : undefined}
        />
      )}
    </div>
  );
}
